/**
 * Governance helpers for the admin console (contract §10 stream S7): the sensitivity-label policy choices, the Office
 * card's switches (per-host flags, automatic Office edits, Microsoft 365 reach) in plain language, the Office installs
 * list, and the audit export link. No React here, so all of it is unit-tested (apps/admin/test/governance.test.ts).
 *
 * The flags are the generic feature flags (GET/PUT /v1/admin/flags); this file only says what each one means and what
 * an unset one does on the service (contract §4.4): a host switch and `office-auto-writes` are ON until an administrator
 * stores a value, every Microsoft 365 reach flag is OFF until it is stored exactly on.
 */
import type { AppPolicy, FeatureFlag } from './api';
import { errorText } from './errors';

// ---- Sensitivity labels ----

export type LabelPolicy = 'allow-audit' | 'warn' | 'block';

/** GET/PUT /v1/admin/label-policy (gyro-svc governanceRoutes.ts). */
export interface LabelPolicyStatus {
  policy: LabelPolicy;
  default: LabelPolicy;
  options: LabelPolicy[];
  /** How long a confirmed release lets content with that label through (warn). */
  releaseMinutes: number;
}

export interface LabelPolicyChoice {
  value: LabelPolicy;
  title: string;
  description: string;
}

/** The three choices, in the order the card shows them, each said the way IT will explain it to a colleague. */
export function labelPolicyChoices(releaseMinutes = 30): LabelPolicyChoice[] {
  return [
    {
      value: 'allow-audit',
      title: 'Allow and record (default)',
      description:
        'Gyro uses labelled content like anything else. Every sensitivity label a read gives Gyro is written to the audit ' +
        'trail (the label id and how many items, never the content); the list below says which reads give one. This is ' +
        'the owner\'s decision of 2026-10-04.',
    },
    {
      value: 'warn',
      title: 'Ask the person first',
      description:
        'A file or Copilot search result that carries a sensitivity label is held back from the AI. The person sees a ' +
        `confirm card that names the label; only if they confirm can Gyro use content with that label, for ${releaseMinutes} ` +
        'minutes, and only for them. Cancelling keeps it held back. Mail, chat, calendar and the other reads listed below ' +
        'carry no label and are not held back.',
    },
    {
      value: 'block',
      title: 'Never use labelled content',
      description:
        'A file or Copilot search result that carries a sensitivity label never reaches the AI. Gyro tells the person which ' +
        'label stopped it and carries on without it. Mail, chat, calendar and the other reads listed below carry no label ' +
        'and still reach the AI.',
    },
  ];
}

/**
 * What the policy covers, so nobody reads more into the setting than it does. The policy acts only on a label that a
 * read returns with its result (contract §4.6); it never looks one up. Checked on 2026-10-04 against the desktop reads
 * (Gyro.Office), the Office add-in's reads (apps/office-addin) and the contract's host context shapes (§6.3): only file
 * reads (the file's own label metadata on the desktop, SharePoint's label extraction in the add-in), Copilot retrieval
 * hits and Outlook's compose item return a label. Outlook gives add-ins no label in read mode, and the mail, chat,
 * calendar and search reads select none.
 */
export const LABEL_POLICY_SCOPE = {
  labelledLead: 'Only a result that carries a sensitivity label can be held back or blocked. Today these reads return one:',
  labelled: [
    'Files read from SharePoint or OneDrive (m365_file_read), on the desktop app and in the Office add-in.',
    'Copilot retrieval search results (m365_retrieve).',
    "The Office add-in's own read of an email or meeting being written in Outlook (office_get_context). The draft the " +
      'add-in sends along with each question is not covered (below).',
  ],
  unlabelledLead: 'These carry no label, so they reach the AI whatever this setting says:',
  unlabelled: [
    'Mail, Teams chat and calendar reads, on the desktop app and in the Office add-in.',
    'Microsoft 365 search, people, tasks, Teams channels, meeting transcripts and catch-up.',
    'Word, Excel and PowerPoint reads in the Office add-in, and an email or meeting open for reading in Outlook (Outlook ' +
      'gives add-ins no label in read mode).',
    'The open email, draft, document, sheet or slide the Office add-in sends along with each question.',
    'Every other system Gyro connects to.',
  ],
} as const;

export function isLabelPolicy(value: unknown): value is LabelPolicy {
  return value === 'allow-audit' || value === 'warn' || value === 'block';
}

// ---- The Office card: app policy and switches ----

export type OfficeFlagGroup = 'hosts' | 'edits' | 'reach';

export interface OfficeFlagSpec {
  key: string;
  group: OfficeFlagGroup;
  title: string;
  description: string;
  /** What the service does while no value is stored (contract §4.4). */
  defaultOn: boolean;
}

export const OFFICE_FLAG_SPECS: readonly OfficeFlagSpec[] = [
  {
    key: 'office-host-outlook',
    group: 'hosts',
    title: 'Gyro in Outlook',
    description: 'The Gyro pane in Outlook: reading the open email, drafting replies and new messages. Off: the Outlook pane tells people Gyro is switched off there.',
    defaultOn: true,
  },
  {
    key: 'office-host-word',
    group: 'hosts',
    title: 'Gyro in Word',
    description: 'The Gyro pane in Word: reading the document, changing it throughout and inserting text as tracked changes, and adding comments.',
    defaultOn: true,
  },
  {
    key: 'office-host-excel',
    group: 'hosts',
    title: 'Gyro in Excel',
    description: 'The Gyro pane in Excel: reading ranges, formulas into empty cells, tables, charts and pivot tables on new sheets.',
    defaultOn: true,
  },
  {
    key: 'office-host-powerpoint',
    group: 'hosts',
    title: 'Gyro in PowerPoint',
    description: 'The Gyro pane in PowerPoint: reading slides and adding new ones.',
    defaultOn: true,
  },
  {
    key: 'office-auto-writes',
    group: 'edits',
    title: 'Low-risk edits without a card',
    description:
      'Inserting and changing text as tracked changes, comments, formulas into empty cells, tables, charts, new slides and opening drafts ' +
      'happen straight away and are reported. Sending, deleting and meetings always ask first, and Gyro never sends email. ' +
      'Off: every Office edit shows a confirm card first.',
    defaultOn: true,
  },
  {
    key: 'office-m365-read',
    group: 'reach',
    title: 'Mail, Teams chats and calendar from Office',
    description: "The Office pane reads the person's own mail, Teams chats and calendar with their own sign-in. Needs admin consent for 'Gyro Office Add-in'.",
    defaultOn: false,
  },
  {
    key: 'office-m365-reach',
    group: 'reach',
    title: 'Microsoft 365 search from Office',
    description:
      "The Office pane searches what the person can already open: SharePoint and OneDrive files, people, To Do, Teams channels and " +
      "meeting transcripts. Needs admin consent; Copilot retrieval also needs a Copilot licence or pay-as-you-go.",
    defaultOn: false,
  },
  {
    key: 'office-m365-write',
    group: 'reach',
    title: 'Microsoft 365 actions from Office',
    description:
      'Tasks, mail flags and categories, new mail folders, moving mail, meetings, rules and automatic replies. Tasks, flags and ' +
      'folders run straight away; everything else asks first. Needs admin consent.',
    defaultOn: false,
  },
  {
    key: 'desktop-m365-reach',
    group: 'reach',
    title: 'Microsoft 365 search on the desktop app',
    description:
      "The desktop app's version of Microsoft 365 search, run on the PC with the person's own sign-in. Needs consent for the " +
      "'Gyro Desktop M365 Reader' scopes added on 2026-10-04.",
    defaultOn: false,
  },
];

const OFFICE_FLAG_KEYS: ReadonlySet<string> = new Set(OFFICE_FLAG_SPECS.map((spec) => spec.key));

/**
 * The stored flags the generic Feature flags table edits: every one except the Office card's switches. One editor per
 * key: the Governance screen keeps one copy of the flags for both, and the Office card edits its keys with their
 * defaults spelled out, so the generic table never sends a stale `enabled` for one of them.
 */
export function genericFlagEntries(flags: Record<string, FeatureFlag>): Array<[string, FeatureFlag]> {
  return Object.entries(flags).filter(([key]) => !OFFICE_FLAG_KEYS.has(key));
}

/** What the flag tables say when GET /v1/admin/flags failed, instead of showing defaults as if they were stored. */
export function flagsReadErrorText(error: unknown): string {
  const status = (error as { status?: unknown } | null | undefined)?.status;
  if (status === 401 || status === 403) return 'The feature flags are visible to IT admins only.';
  return error instanceof Error && error.message.trim() ? `The feature flags could not be read: ${error.message}` : 'The feature flags could not be read.';
}

export const OFFICE_FLAG_GROUP_TITLES: Record<OfficeFlagGroup, string> = {
  hosts: 'Office apps',
  edits: 'Edits',
  reach: 'Microsoft 365 reach',
};

/** Roles cannot stage these: Office and desktop callers carry no roles. Stage with percent. */
export const OFFICE_FLAG_ROLES_NOTE = 'Roles cannot limit these switches (Office and desktop callers carry no roles); use the percentage to stage a rollout.';

export interface OfficeFlagState {
  /** Whether an administrator stored a value (otherwise the default applies). */
  stored: boolean;
  /** The checkbox: on for at least some people. */
  on: boolean;
  /** The rollout percentage shown (100 when not stored or not given). */
  percent: number;
  /** One sentence: who has it now. */
  summary: string;
}

function percentOf(flag: FeatureFlag): number {
  const value = typeof flag.percent === 'number' && Number.isFinite(flag.percent) ? flag.percent : 100;
  return Math.min(100, Math.max(0, Math.round(value)));
}

/** What a switch does for people now, from the stored flag (or its default when none is stored). */
export function officeFlagState(spec: OfficeFlagSpec, flag: FeatureFlag | undefined): OfficeFlagState {
  if (!flag) {
    return {
      stored: false,
      on: spec.defaultOn,
      percent: 100,
      summary: spec.defaultOn ? 'On for everyone (not set; on by default).' : 'Off (not set; stays off until switched on).',
    };
  }
  const percent = percentOf(flag);
  if (!flag.enabled) return { stored: true, on: false, percent, summary: 'Off for everyone.' };
  if (percent >= 100) return { stored: true, on: true, percent, summary: 'On for everyone.' };
  if (percent <= 0) return { stored: true, on: true, percent, summary: 'On at 0%: nobody has it yet.' };
  return { stored: true, on: true, percent, summary: `On for ${percent}% of people, off for the rest.` };
}

/**
 * The body the card sends for a switch: the current state with one change applied. Never roles (the service refuses
 * them on these keys); the percentage is kept, and a typed one is clamped to a whole 0-100.
 */
export function officeFlagPatch(current: Pick<OfficeFlagState, 'on' | 'percent'>, change: { on?: boolean; percent?: number }): FeatureFlag {
  const percent = change.percent === undefined
    ? current.percent
    : Math.min(100, Math.max(0, Math.round(Number.isFinite(change.percent) ? change.percent : 0)));
  return { enabled: change.on ?? current.on, percent };
}

/** The outcome of reading the office app's policy from Apps & Surfaces (GET /v1/admin/apps, IT admins only). */
export type OfficeAppRead =
  | { status: 'loading' }
  | { status: 'failed'; httpStatus?: number }
  | { status: 'loaded'; app: AppPolicy | undefined };

/** A failed GET /v1/admin/apps, keeping the HTTP status when the console's API helper gave one. */
export function officeAppReadFailure(error: unknown): OfficeAppRead {
  const status = (error as { status?: unknown } | null | undefined)?.status;
  return typeof status === 'number' ? { status: 'failed', httpStatus: status } : { status: 'failed' };
}

export interface OfficeAppSummary {
  /**
   * `registered`: the office app's policy, in lines. `absent`: Apps & Surfaces answered and this service has no office
   * app. `unknown`: the read failed. `loading`: not answered yet (no lines).
   */
  state: 'registered' | 'absent' | 'unknown' | 'loading';
  lines: string[];
}

/**
 * The office app's central policy in a few plain lines. The card says the add-in is not switched on only when Apps &
 * Surfaces answered without an office app; while it is being read, or when the read failed (an auditor's 403, a 5xx, the
 * network), it says the state is not known instead of stating that the add-in is off.
 */
export function officeAppSummary(read: OfficeAppRead): OfficeAppSummary {
  if (read.status === 'loading') return { state: 'loading', lines: [] };
  if (read.status === 'failed') {
    return {
      state: 'unknown',
      lines: [
        read.httpStatus === 401 || read.httpStatus === 403
          ? 'Whether the Office add-in is switched on could not be read: Apps & Surfaces is visible to IT admins only.'
          : 'Whether the Office add-in is switched on could not be read (Apps & Surfaces did not answer). Reload the page to try again.',
      ],
    };
  }
  const app = read.app;
  if (!app) {
    return {
      state: 'absent',
      lines: [
        'The Office add-in is not switched on in this service (GYRO_OFFICE_ENABLED). Its switches below are stored now and ' +
          'apply once it is. The desktop app\'s Microsoft 365 search switch (desktop-m365-reach) does not depend on it and ' +
          'applies now.',
      ],
    };
  }
  const lines = [
    app.enabled ? 'Gyro in Office is on (Apps & Surfaces).' : 'Gyro in Office is OFF for everyone (Apps & Surfaces): every pane refuses with office_disabled.',
  ];
  if (app.autonomy === 'propose') lines.push('Autonomy override: propose. Every Office edit asks first, whatever the edits switch says.');
  else if (app.autonomy === 'off') lines.push('Autonomy override: off. Gyro proposes no Office edits at all.');
  else lines.push('Autonomy: low-risk edits may run without a card (standard risk, reversible only); the edits switch below can turn that off.');
  if (app.approvalRequiredAtOrAbove) lines.push(`Edits at ${app.approvalRequiredAtOrAbove} risk or above need a second approver.`);
  return { state: 'registered', lines };
}

// ---- Office installs ----

export type OfficeHost = 'outlook' | 'word' | 'excel' | 'powerpoint';
export type OfficeInstallStatus = 'active' | 'revoked' | 'retired';

/** GET /v1/admin/office/installs rows (contract §1.4-§1.5): no key material. Times are epoch milliseconds. */
export interface OfficeInstall {
  installId: string;
  publicKeySha256: string;
  ownerObjectId: string;
  ownerEmail: string;
  tenantId: string;
  host: OfficeHost | string;
  platform: string;
  officeVersion: string;
  addinVersion: string;
  status: OfficeInstallStatus;
  createdAt: number;
  lastSeenAt: number;
  revokedAt?: number;
  revokedBy?: string;
  retiredReason?: 'cap' | 'idle';
}

const HOST_LABELS: Record<string, string> = { outlook: 'Outlook', word: 'Word', excel: 'Excel', powerpoint: 'PowerPoint' };

export function officeHostLabel(host: string): string {
  return HOST_LABELS[host] ?? host;
}

/** "3 minutes ago", "2 days ago"; a future or unknown time reads "just now" / "unknown". */
export function formatAge(at: number | undefined, now: number): string {
  if (typeof at !== 'number' || !Number.isFinite(at) || at <= 0) return 'unknown';
  const seconds = Math.max(0, Math.round((now - at) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/** The status column, in words. */
export function installStatusText(install: Pick<OfficeInstall, 'status' | 'revokedBy' | 'retiredReason'>): string {
  if (install.status === 'active') return 'Active';
  if (install.status === 'revoked') return install.revokedBy ? `Revoked by ${install.revokedBy}` : 'Revoked';
  if (install.retiredReason === 'cap') return 'Retired (the person passed the per-person install limit)';
  if (install.retiredReason === 'idle') return 'Retired (unused for 90 days)';
  return 'Retired';
}

/** Active installs first, then the most recently seen. */
export function sortInstalls(rows: readonly OfficeInstall[]): OfficeInstall[] {
  const rank = (row: OfficeInstall) => (row.status === 'active' ? 0 : 1);
  return [...rows].sort((left, right) => rank(left) - rank(right) || right.lastSeenAt - left.lastSeenAt || left.installId.localeCompare(right.installId));
}

/** Rows whose person, install id, host or platform contains the search text (case-insensitive). */
export function filterInstalls(rows: readonly OfficeInstall[], search: string): OfficeInstall[] {
  const needle = search.trim().toLowerCase();
  if (!needle) return [...rows];
  return rows.filter((row) => [row.ownerEmail, row.ownerObjectId, row.installId, officeHostLabel(row.host), row.platform].some((value) => String(value ?? '').toLowerCase().includes(needle)));
}

/** The confirmation the admin answers before a revoke (window.confirm). */
export function revokeConfirmText(install: Pick<OfficeInstall, 'ownerEmail' | 'host' | 'platform' | 'installId'>): string {
  return `Revoke ${install.ownerEmail}'s Gyro add-in in ${officeHostLabel(install.host)} (${install.platform || 'unknown platform'})?\n\n` +
    'That install stops working at once and stays revoked. If the person opens the add-in again it enrols as a new install ' +
    `under their own sign-in.\n\nInstall ${install.installId}`;
}

/**
 * Whether the console should show the Office installs screen, from the probe's outcome: shown when the list answered, or
 * failed for a reason the screen itself should explain; hidden when the service has no Office routes (404, the add-in
 * is not enabled or not deployed yet) or the person may not list installs (401/403: auditors).
 */
export function officeInstallsVisible(probeError: unknown): boolean {
  if (probeError === null || probeError === undefined) return true;
  const status = (probeError as { status?: unknown }).status;
  return !(status === 404 || status === 401 || status === 403);
}

/**
 * The Office installs tab's state from the signed-in role and the list probe's answer (null until it answers): an
 * admin waiting on the probe sees `checking` (a deep link shows a spinner, never "not enabled"), then `shown` or
 * `hidden`; anyone who is not an admin never probes and never sees the tab.
 */
/** The most rows GET /v1/admin/office/installs returns (the service slices its list there). */
export const OFFICE_INSTALLS_LIST_CAP = 1_000;

/**
 * The value for the list route's `?user=` filter when the search box holds one person (an e-mail address or an Entra
 * object id), else undefined: the service then filters before its row cap, so a person beyond the first 1,000 rows
 * is still found.
 */
export function installsPersonLookup(search: string): string | undefined {
  const value = search.trim();
  if (value.length === 0 || value.length > 320) return undefined;
  if (/^[^\s@]+@[^\s@]+$/.test(value)) return value.toLowerCase();
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) return value.toLowerCase();
  return undefined;
}

/** A note when the unfiltered list hit the service's row cap, so nobody reads "not listed" as "no install". */
export function installsCapNote(rowCount: number, personFilter: string | undefined): string | null {
  if (personFilter || rowCount < OFFICE_INSTALLS_LIST_CAP) return null;
  return `Showing the first ${OFFICE_INSTALLS_LIST_CAP.toLocaleString('en-US')} installs, the most the service lists at once. ` +
    'Look a person up by e-mail address or object id to see all of theirs.';
}

export function officeInstallsTab(isAdmin: boolean, probe: boolean | null): 'checking' | 'shown' | 'hidden' {
  if (!isAdmin) return 'hidden';
  if (probe === null) return 'checking';
  return probe ? 'shown' : 'hidden';
}

// ---- Audit export ----

export interface AuditExportFilter {
  from?: string;
  to?: string;
  event?: string;
  app?: string;
}

/** The download link for GET /v1/admin/audit/export (the session cookie authorizes it; same origin). */
export function auditExportHref(filter: AuditExportFilter = {}): string {
  const params = new URLSearchParams({ format: 'ndjson' });
  for (const key of ['from', 'to', 'event', 'app'] as const) {
    const value = filter[key]?.trim();
    if (value) params.set(key, value);
  }
  return `/v1/admin/audit/export?${params.toString()}`;
}

// ---- Right to erasure ----

export interface BackgroundErasureProgress {
  requestId: string;
  requestedAt: string;
  memoryContext: { state: 'pending' | 'completed'; rowsRedacted: number; checkedAt?: string };
  pendingFamilies: string[];
}
export interface BackgroundErasureReview {
  state: 'reviewed' | 'unavailable' | 'identity-required' | 'owner-not-recorded';
  message: string;
  accountDeletionConfirmed: false;
  subjectId?: string;
  reviewHash?: string;
  counts?: { memory: number; contexts: number; instructions: number };
  protectedOperations?: { unfinishedRuns: number; unresolvedRuns: number };
  progress?: BackgroundErasureProgress;
}
export interface BackgroundErasureResult {
  state: 'pending' | 'not-requested';
  message: string;
  accountDeletionConfirmed: false;
  subjectId?: string;
  progress?: BackgroundErasureProgress;
}
export interface BackgroundErasureSubmission { subjectId: string; reviewHash: string }
/** Only an explicit, complete review can authorize the background cleanup. */
export function backgroundErasureSubmission(review: BackgroundErasureReview): BackgroundErasureSubmission | null {
  return review.state === 'reviewed' && typeof review.subjectId === 'string'
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(review.subjectId)
    && typeof review.reviewHash === 'string' && /^[0-9a-f]{64}$/.test(review.reviewHash)
    ? { subjectId: review.subjectId, reviewHash: review.reviewHash } : null;
}
export function backgroundErasureText(background?: BackgroundErasureResult): string {
  if (!background) return 'Background task cleanup was not confirmed. Full erasure remains unresolved; this does not delete the account.';
  return `${background.message} ${background.state === 'pending' ? 'Background task cleanup remains pending.' : 'Background task cleanup was not requested.'} Full erasure is not complete. This does not delete the account.`;
}
export function backgroundErasureFamilyText(family: string): string {
  const labels: Record<string, string> = {
    'memory-and-context-bodies': 'Saved background notes and context copies',
    'daily-discovery': 'Collected briefing source content',
    'daily-finalization': 'Briefing assembly source copies',
    'matching-inputs': 'Task matching input copies',
    'deadline-inputs': 'Deadline extraction input copies',
    'deadline-review': 'Deadline review content',
    'observation-preparation': 'Original task preparation inputs',
    'canonical-register': 'Task, source excerpt and Daily Brief content',
    'mission-narrative-rows': 'Responsibility briefs, success criteria and required topics',
    'saved-definition-authored-bodies': 'Saved companion setup text and profile reply copies',
    'profiles-and-definitions': 'Task profiles and definitions',
    'mission-specifications': 'Responsibility settings',
    'other-private-bodies': 'Other private background data',
    'device-copies': 'Copies held on devices',
    'connector-closure': 'Connection closure',
    'protected-operation-identities': 'Protected operation and accounting records',
    'published-package-attribution': 'Published template attribution',
    'commercial-entitlements': 'Purchase and entitlement records',
  };
  return labels[family] ?? 'Other retained background task data';
}

/**
 * DELETE /v1/admin/erase's answer. `outfitPicks` and `outfitRatings` are the person's desktop mascot records: absent
 * from a gyro-svc from before them, and null when the id names no Entra object id the service knows (they are kept by
 * object id, so none could be looked up).
 */
export interface EraseResult {
  removed: number;
  status?: 'partial';
  accountDeletionConfirmed?: false;
  background?: BackgroundErasureResult;
  /** Present only when the notification center is enabled. A partial cleanup must remain visible. */
  attentionErasure?: 'complete' | 'incomplete';
  memoryNotes?: number;
  /**
   * The person's saved skills erased; null when the id names no single Entra object id Gyro knows (none were looked
   * up); absent from a gyro-svc from before saved skills.
   */
  skills?: number | null;
  /** Company skills promoted from the person's skills that no longer name them (their author name stays). */
  companyUnlinked?: number;
  toolPermissions?: number;
  codeOutputs?: number;
  outfitPicks?: number | null;
  outfitRatings?: number | null;
  /** Whether the PPP store held anything of this person's; absent from a gyro-svc from before PPP. */
  pppFound?: boolean;
  /** The PPP reports erased. */
  pppReports?: number;
  /** An object id that no PPP row carries and for which Gyro found no address: grants and digests naming the person by e-mail were not looked up. */
  pppUnresolvedOid?: boolean;
  /**
   * Addresses Gyro holds for the object id that it left alone because they were reissued: another person's PPP records
   * or Gyro's records name them now, so erasing by them would remove that person's records.
   */
  pppReissuedAddresses?: number;
}

/**
 * The note after an erase, in plain words. An erase that found no outfit records for the id says so and how to reach
 * them (by the person's Entra object id), never "0". Unlinked ratings stay in the averages without the person's name,
 * which the note says, so a count that does not drop is not taken for an erase that failed. An address left alone
 * because it was reissued is said, and the note then never suggests erasing again by e-mail (that would reach the
 * address's new holder).
 */
const ATTENTION_ERASURE_WARNING = ' Question and notification data was NOT confirmed erased. Run the erase again with the person\'s Entra object ID; repeating it is safe.';

/** Keep optional cleanup failures visible even when a later legacy erase step also fails. */
export function eraseErrorText(error: unknown, fallback = 'Nothing was erased.'): string {
  const text = errorText(error, fallback);
  const body = (error as { body?: { attentionErasure?: unknown } } | null | undefined)?.body;
  return body?.attentionErasure === 'incomplete' ? text + ATTENTION_ERASURE_WARNING : text;
}

export function eraseResultText(result: EraseResult, id: string): string {
  const parts = [`${result.removed} conversation(s)`, `${result.memoryNotes ?? 0} memory note(s)`];
  if (typeof result.skills === 'number') parts.push(`${result.skills} saved skill(s)`);
  const picks = result.outfitPicks;
  const ratings = result.outfitRatings;
  const outfitsFound = typeof picks === 'number' && typeof ratings === 'number';
  if (outfitsFound) parts.push(`${picks} outfit pick(s)`, `${ratings} outfit rating(s)`);
  if (result.pppFound === true) parts.push(`${result.pppReports ?? 0} PPP report(s)`);
  parts.push(`${result.codeOutputs ?? 0} set(s) of files Gyro's code created`);
  let text = `Removed ${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]} for ${id}.`;
  if (outfitsFound && typeof ratings === 'number' && ratings > 0) text += ' Their star ratings no longer name them; the stars stay in the outfits\' averages.';
  const reissued = typeof result.pppReissuedAddresses === 'number' && result.pppReissuedAddresses > 0 ? result.pppReissuedAddresses : 0;
  if (result.pppFound === false) {
    // PPPs built from a web app are kept under the person's e-mail. An object-id erase already reaches that address when
    // the service could tie the id to one, so the hint to erase again by e-mail is only for an object id it could not
    // (pppUnresolvedOid), and never when the address was reissued.
    text += result.pppUnresolvedOid === true && !id.includes('@') && reissued === 0
      ? " No PPP records were found for this id (erase again with the person's e-mail if they built PPPs from a web app)."
      : ' No PPP records were found for this id.';
  }
  if (reissued > 0) {
    text += reissued === 1
      ? ' PPP records under the e-mail address Gyro holds for this person were left alone: that address now belongs to someone else, so erasing it would remove their records.'
      : ` PPP records under the ${reissued} e-mail addresses Gyro holds for this person were left alone: those addresses now belong to someone else, so erasing them would remove their records.`;
  }
  if (typeof result.companyUnlinked === 'number' && result.companyUnlinked > 0) {
    text += result.companyUnlinked === 1
      ? ' One company skill promoted from their skills no longer names them; its author name stays as published.'
      : ` ${result.companyUnlinked} company skills promoted from their skills no longer name them; their author names stay as published.`;
  }
  const outfitsUnresolved = picks === null || ratings === null;
  const skillsUnresolved = result.skills === null;
  if (outfitsUnresolved && skillsUnresolved) {
    text += ' Outfit picks, star ratings and saved skills are kept by Entra object id, and Gyro could not tie this id to one person\'s object id, so none were erased. Erase again with the person\'s object id to remove them.';
  } else if (outfitsUnresolved) {
    text += ' Outfit picks and star ratings are kept by Entra object id, and Gyro knows no object id for this id, so none were erased. Erase again with the person\'s object id to remove them.';
  } else if (skillsUnresolved) {
    text += ' Saved skills are kept by Entra object id, and Gyro could not tie this id to one person\'s object id, so none were erased. Erase again with the person\'s object id to remove them.';
  }
  if (result.attentionErasure === 'incomplete') {
    text += ATTENTION_ERASURE_WARNING;
  }
  return text;
}
